import { useCallback, useMemo } from 'react';
import DrawerExtraButtons from 'components/DrawerExtraButtons';
import useSelectResource from 'hooks/useSelectResource';
import { getText } from 'i18n/intl';
import {
  changeMembership,
  checkMembershipSaveDisabled,
  deleteMembership,
  discardChanges,
  firstMembershipSelector,
  membershipMapSelector,
  saveMembership,
  secondMembershipSelector,
  setFirstMembership,
  setSecondMembership,
} from 'slices/memberships.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { TMemberType } from '@timeedit/types';
import { TEDrawer } from '@timeedit/ui-components';
import Membership from './Membership';

interface Props {
  confirmDiscardChanges: (isFirstDrawer: boolean, actionFn: () => void) => void;
}

const MembershipDrawer = ({ confirmDiscardChanges }: Props) => {
  const dispatch = useAppDispatch();

  const firstMembership = useAppSelector(firstMembershipSelector);
  const secondMembership = useAppSelector(secondMembershipSelector);
  const membershipMap: Record<number | string, TMemberType> = useAppSelector(
    membershipMapSelector
  );

  const setFirstResource = useCallback(
    (membership: TMemberType) => dispatch(setFirstMembership(membership)),
    [dispatch]
  );

  const setSecondResource = useCallback(
    (membership: TMemberType) => dispatch(setSecondMembership(membership)),
    [dispatch]
  );

  useSelectResource(
    'integer',
    membershipMap,
    firstMembership,
    secondMembership,
    setFirstResource,
    setSecondResource
  );

  const firstMembershipSaveDisabled = useMemo(
    () => dispatch(checkMembershipSaveDisabled(true)),
    [firstMembership]
  );

  const secondMembershipSaveDisabled = useMemo(
    () => dispatch(checkMembershipSaveDisabled(false)),
    [secondMembership]
  );

  const onDelete = useCallback((membership: TMemberType) => {
    dispatch(deleteMembership(membership.id));
  }, []);

  const onSave = useCallback((membership: TMemberType) => {
    dispatch(saveMembership(membership));
  }, []);

  const onClose = useCallback(
    (isFirstDrawer: boolean) =>
      confirmDiscardChanges(isFirstDrawer, () => {
        if (isFirstDrawer) {
          dispatch(setFirstMembership(undefined));
        } else {
          dispatch(setSecondMembership(undefined));
        }
      }),
    []
  );

  const onChange = useCallback(
    (isFirstDrawer: boolean, key: string, value: any) =>
      dispatch(
        changeMembership({
          isFirstDrawer,
          key: key as keyof TMemberType,
          value,
        })
      ),
    []
  );

  return (
    <TEDrawer
      expandWidth="70vw"
      firstDrawer={{
        title: firstMembership?.name || '',
        content: (
          <Membership
            membership={firstMembership}
            onChange={(key: string, value: any) => onChange(true, key, value)}
          />
        ),
        drawerOpen: !!firstMembership,
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText('membership_confirmation_delete')}
            handleDelete={() => onDelete(firstMembership!)}
            handleDiscard={() =>
              dispatch(
                discardChanges({
                  isFirstDrawer: true,
                  id: firstMembership!.id,
                })
              )
            }
            handleSave={() => onSave(firstMembership!)}
            isSaveDisable={firstMembershipSaveDisabled as any}
          />
        ),
        setDrawerOpen: (open) => !open && onClose(true),
      }}
      secondDrawer={{
        title: secondMembership?.name || '',
        content: (
          <Membership
            membership={secondMembership}
            onChange={(key: string, value: any) => onChange(false, key, value)}
          />
        ),
        drawerOpen: !!secondMembership,
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText('membership_confirmation_delete')}
            handleDelete={() => onDelete(secondMembership!)}
            handleDiscard={() =>
              dispatch(
                discardChanges({
                  isFirstDrawer: false,
                  id: secondMembership!.id,
                })
              )
            }
            handleSave={() => onSave(secondMembership!)}
            isSaveDisable={secondMembershipSaveDisabled as any}
          />
        ),
        setDrawerOpen: (open) => !open && onClose(false),
      }}
    />
  );
};

export default MembershipDrawer;
